import {ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {CommonModule} from '@angular/common';
import {MatFormField, MatInput, MatLabel} from '@angular/material/input';
import {ClaimMetadataRequest} from '../../entity/metadata';
import {AuthService} from '../../../authentication/auth-service';
import {ClaimCreatorService} from '../../control/claim-creator-service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {LocalizationPipePipe} from '../../../localization/control/localisation-pipe-pipe';
import {MatOption, MatSelect} from '@angular/material/select';
import {ObjectType} from '../../entity/claim-item';
import {ResidenceAutocompletionService} from '../../control/residence-autocompletion-service';


@Component({
  selector: 'app-entire-building-claim-metadata',
  imports: [
    CommonModule,
    MatFormField,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    MatProgressSpinner,
    LocalizationPipePipe,
    MatSelect,
    MatOption
  ],
  templateUrl: './entire-building-claim-metadata.html',
  styleUrl: './entire-building-claim-metadata.scss'
})
export class EntireBuildingClaimMetadata {
  @Output() afterClaimCreation = new EventEmitter<ClaimMetadataRequest>();
  @Output() formValidityChange = new EventEmitter<boolean>();
  @Input() claimId?: string;
  isSubmitting: boolean = false;
  readonly metadataForm: FormGroup;
  protected error?: string;
  protected readonly claimTypes = ['Earthquake', 'Flood'];
  protected readonly objectType: ObjectType = ObjectType.Residence;

  constructor(
    private fb: FormBuilder,
    private readonly claimService: ClaimCreatorService,
    private readonly cdr: ChangeDetectorRef,
    private readonly residenceAutocompletionService: ResidenceAutocompletionService,
    authService: AuthService,
  ) {
    this.metadataForm = this.fb.group({
      adjusterID: [authService.getCurrentUser()?.profile?.sub],
      adjusterCompany: [authService.getCurrentUser()?.profile?.['custom:adjuster_company_id']],
      address: ['', [Validators.required]],
      unitOwnerName: ['', [Validators.required]],
      claimNumber: ['', [Validators.required]],
      policyLimit: [0, [Validators.required, Validators.min(0)]],
      claimType: ['Earthquake', [Validators.required]]
    });

    this.metadataForm.valueChanges.subscribe(() => {
      this.error = undefined;
    })

    // Subscribe to form validity changes
    this.metadataForm.statusChanges.subscribe(() => {
      this.formValidityChange.emit(this.metadataForm.valid);
    });

    this.metadataForm.controls['claimNumber'].valueChanges.subscribe(v => {
      if (v) {
        this.residenceAutocompletionService.getPolicyFor(v)
          .then(policy => {
            this.metadataForm.controls['policyLimit'].setValue(policy?.policyLimit ?? 0, {emitEvent: false});
            this.metadataForm.controls['address'].setValue(policy?.address ?? '', {emitEvent: false});
            this.metadataForm.controls['unitOwnerName'].setValue(policy?.policyHolderName ?? '', {emitEvent: false});
            this.metadataForm.controls['claimType'].setValue('Flood', {emitEvent: false});
          })
          .catch(error => console.error(error))
      }
    });

    // Emit initial validity state
    setTimeout(() => {
      this.formValidityChange.emit(this.metadataForm.valid);
    });
  }

  onInitiateClaim(): void {
    if (this.metadataForm.valid) {
      const formValues = this.metadataForm.value;

      const claim_combined_id: string = `${formValues.claimNumber}_${formValues.unitOwnerName}`.replace(/\s/g, '_').toLowerCase()
      const mappedData: ClaimMetadataRequest = {
        adjusterID: formValues.adjusterID,
        adjusterCompany: formValues.adjusterCompany,
        insuranceClaimReference: claim_combined_id,
        lossAdjusterClaimReference: claim_combined_id,
        condominiumName: claim_combined_id,
        address: formValues.address,
        unitOwnerName: formValues.unitOwnerName,
        unitNumber: "1", // Default unit for entire building claims
        claimType: this.mapClaimTypeToBackend(formValues.claimType),
        policyLimit: Math.max(formValues.policyLimit ?? 0, 0),
        policyNumber: formValues.claimNumber ?? "",
        objectType: this.objectType
      };

      this.createClaimDraft(mappedData);
    } else {
      Object.keys(this.metadataForm.controls).forEach(key => {
        const control = this.metadataForm.get(key);
        if (control && control.invalid) {
          control.markAsTouched();
        }
      });
    }
  }

  /**
   * Maps frontend claim type display names to backend values
   */
  private mapClaimTypeToBackend(frontendClaimType: string): string {
    const mapping: Record<string, string> = {
      'Earthquakes': 'earthquake',
      'Earthquake': 'earthquake',
      'Flood': 'flood',
      'Fire': 'fire',
      'Storm': 'storm',
      'Wind': 'wind',
      'Other': 'other'
    };

    return mapping[frontendClaimType] ?? 'earthquake'; // Default fallback
  }

  createClaimDraft(metadataForm: ClaimMetadataRequest) {
    if (this.claimId !== undefined) {
      metadataForm = {...metadataForm, claimID: this.claimId}
    }
    this.error = undefined;
    this.isSubmitting = true;
    this.cdr.detectChanges();
    const request: ClaimMetadataRequest = {} as ClaimMetadataRequest;
    for (const key in metadataForm) {
      if (key !== 'policyLimit') {
        request[key] = (metadataForm[key as keyof ClaimMetadataRequest] ?? '').toString();
      } else {
        request[key] = metadataForm[key as keyof ClaimMetadataRequest] as number;
      }
      console.log(`request[${key} as keyof ClaimMetadataRequest] = (${metadataForm[key as keyof ClaimMetadataRequest]} ?? '')`)
    }
    this.claimService.createClaimDraft(request)
      .then(r => {
        console.log('createClaimDraft done', request)
        this.afterClaimCreation.emit({...r});
      })
      .catch(error => {
        this.isSubmitting = false;
        this.error = error.message;
      })
      .finally(() => {
        this.isSubmitting = false;
        this.cdr.detectChanges();
      })
  }
}
